<template>
    <div class="hero">
        <TransitionEffect>
            <EditProfileModal
                v-if="editProfileModal"
                :currentUser="currentUser"
                :id="id"
                @updateProfile="updateProfile"
                @close="editProfileModal=false"/>
        </TransitionEffect>
        <TransitionEffect>
            <UserList v-if="followersListDialog"
                :heading="followersHeading"
                :userIDs="social.followers.users"
                @close="followersListDialog=false"/>
        </TransitionEffect>
        <TransitionEffect>
            <UserList v-if="followingListDialog"
                :heading="siteContent.followedBy + ' ' + currentUser.name"
                :userIDs="social.following.users"
                @close="followingListDialog=false"/>
        </TransitionEffect>

        <div class="cover">
            <img class="coverArtwork"
                loading="lazy"
                :alt="'Cover image of ' + currentUser.name"
                :src="cover"/>
            <img class="avatar"
                loading="lazy"
                :alt="'Avatar of ' + currentUser.name"
                :src="avatar"/>
            <div class="content">
                <div class="editButtonWrapper">
                    <Button class="editButton"
                        v-if="auth.id === id || permissions.isAdmin"
                        @click="editProfileModal=true"
                        size="small"
                        appearance="secondary"
                        prefixIcon="edit">
                        {{ siteContent.editProfile }}
                    </Button>
                </div>
                <div class="userDetails">
                    <div class="nameContainer">
                        <Heading
                            font="secondary"
                            size="xxl">
                            {{ currentUser.name }}
                        </Heading>
                        <FollowButton
                            v-if="auth.id && auth.id !== id"
                            :followers="social.followers.users"
                            :currentUserID="currentUser.id"/>
                    </div>
                    <div class="socials">
                        <Button class="social"
                            v-if="currentUser.instagram"
                            :link="'https://instagram.com/' + currentUser.instagram"
                            appearance="secondary"
                            size="fullWidth"
                            target="blank">
                                <div class="socialIcon instagramIcon"
                                    :alt="'Instagram of ' + currentUser.name"></div>
                        </Button>
                        <Button class="social"
                            v-if="currentUser.website"
                            :link="'https://' + currentUser.website"
                            appearance="secondary"
                            size="fullWidth"
                            target="blank">
                                <Icon class="socialIcon"
                                    icon="public"
                                    appearance="white"/>
                        </Button>
                    </div>
                </div>
            </div>
        </div>
        <div class="intro">
            <div class="socialCount">
                <Button class="followingButton"
                    v-if="social.following.users.length"
                    appearance="secondary"
                    size="fullWidth"
                    @click="followingListDialog=true">
                    <span class="followCount">
                        {{ social.following.users.length }}
                    </span>
                    <span class="followLabel">
                        <template v-if="preferences.language === 'hu'">
                            {{ siteContent.follow }}
                        </template>
                        <template v-else>
                            {{ siteContent.following }}
                        </template>
                    </span>
                </Button>
                <Button
                    v-if="social.followers.users.length"
                    appearance="secondary"
                    size="fullWidth"
                    @click="followersListDialog=true">
                    <span class="followCount">
                        {{ social.followers.users.length }}
                    </span>
                    <span class="followLabel">
                        {{ followersLabel }}
                    </span>
                </Button>
            </div>
            {{ intro }}
        </div>

        <BlurredBackground
            :image="cover"/>
    </div>
</template>

<script>
    import { db } from '@/firebase'

    import EditProfileModal from '@/components/creator/EditProfileModal'
    import FollowButton     from '@/components/templates/FollowButton'
    import UserList         from '@/components/templates/UserList'

    export default {
        name: 'Hero',
        components: {
            EditProfileModal,
            FollowButton,
            UserList
        },
        props: {
            currentUser: Object,
            id: String
        },
        data() {
            return {
                editProfileModal: false,
                followersListDialog: false,
                followingListDialog: false,
                social: {
                    followers: {
                        users: []
                    },
                    following: {
                        users: []
                    }
                }
            }
        },
        computed: {
            cover() {
                if(this.currentUser.cover) {
                    return this.currentUser.cover
                } else {
                    return this.constants.defaults.cover
                }
            },
            avatar() {
                if(this.currentUser.avatar) {
                    return this.currentUser.avatar
                } else {
                    return this.constants.defaults.avatar
                }
            },
            intro() {
                if(this.preferences.language === 'hu' && this.currentUser.intro.hu) {
                    return this.currentUser.intro.hu
                } else {
                    return this.currentUser.intro
                }
            },
            followersLabel() {
                if(this.social.followers.users.length > 1) {
                    return this.siteContent.followers
                } else {
                    return this.siteContent.follower
                }
            },
            followersHeading() {
                if(this.preferences.language === 'hu') {
                    return this.currentUser.name + ' ' + this.siteContent.followersOf
                } else {
                    return this.siteContent.followersOf + ' ' + this.currentUser.name
                }
            }
        },
        async mounted() {
            this.fetchSocialMetrics()
        },
        methods: {
            updateProfile(u) {
                this.$emit('updateProfile', u)
            },
            changeSelected(value) {
                this.$emit('changeSelected', value)
            },
            async fetchSocialMetrics() {
                try {
                    await db
                        .collection('users')
                        .doc(this.currentUser.id)
                        .collection('social')
                        .doc('followers')
                        .onSnapshot((doc) => {
                            const users = doc.data()
                            this.social.followers = users

                            return () => cleanUp()
                        })

                    await db
                        .collection('users')
                        .doc(this.currentUser.id)
                        .collection('social')
                        .doc('following')
                        .onSnapshot((doc) => {
                            const users = doc.data()
                            this.social.following = users

                            return () => cleanUp()
                        })
                } catch(error) {
                    throw error
                }
            }
        },
        watch: {
            // call again the method if currentUser changes
            currentUser() {
                this.fetchSocialMetrics()
            }
        }
    }
</script>

<style scoped lang="scss">
    @import '../../scss/variables';

    .hero {
        position: relative;
        display: flex;
        flex-direction: column;
        width: 100%;
    }

    .avatar {
        margin: 0 2rem -4rem 0;
        width: 8rem;
        height: 8rem;
        z-index: 1;
        display: block;
        flex: none;
        object-fit: cover;
        background: var(--color-grey-200);
        border-radius: 50%;
        box-shadow: var(--shadow-l);
    }

    .editButtonWrapper {
        z-index: 1;
    }

    .cover {
        position: relative;
        display: flex;
        align-items: flex-end;
        height: 20rem;
        width: 100%;
        padding: 1.5rem 1.5rem 1.5rem 4rem;

        .coverArtwork {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
            background: var(--color-grey-200);
            border-radius: 1rem;
        }

        &::after {
            content: '';
            position: absolute;
            width: 100%;
            height: 100%;
            bottom: 0;
            left: 0;
            background: var(--gradient-black-0-black-60-important);
            border-radius: 0 0 1rem 1rem;
        }
    }

    .content {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        align-items: flex-end;
        width: 100%;
        height: 100%;
    }

    .userDetails {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        color: var(--color-white-important);
        z-index: 1;
    }

    .nameContainer {
        display: flex;
        align-items: center;
    }

    .h1 {
        margin: 0;
        text-align: center;
    }

    .socials {
        display: flex;
        color: var(--color-white-important);

        .flat .socialIcon, .flat .instagramIcon {
            margin: 0;
        }

        .social {
            margin-left: 1rem;
        }
    }

    .socialCount {
        display: flex;
        margin-bottom: 1rem;
    }

    .followingButton {
        margin-right: 1rem;
    }

    .followLabel {
        font-weight: 500;
    }

    .intro {
        max-width: 56rem;
        font-size: 1.15rem;
        padding: 2rem 2rem 4rem 14rem;
    }

    .followCount {
        margin-right: 0.5rem;
        font-size: 1.15rem;
    }

    .followButton {
        margin-left: 1.5rem;
    }

    @media all and (max-width: 1440px) {
        .cover {
            padding-left: 2rem;
        }

        .intro {
            padding-left: 12rem;
        }
    }

    @media all and (max-width: 640px) {
        .cover {
            flex-direction: column;
            align-items: center;
            justify-content: flex-end;
            padding: 2rem 1rem;
        }

        .content {
            margin: 0;
            height: auto;
        }

        .avatar {
            margin: 0 0 1rem 0;
            width: 6rem;
            height: 6rem;
        }

        .userDetails {
            flex-direction: column-reverse;
        }

        .editButtonWrapper {
            position: absolute;
            top: 0.5rem;
            right: 0.5rem;
        }

        .socials {
            position: absolute;
            left: 0.5rem;
            top: 0.5rem;

            .social {
                &:first-of-type {
                    margin: 0;
                }
            }
        }

        .intro {
            padding: 2rem 1rem 3rem 1rem;
            text-align: center;
        }

        .nameContainer {
            flex-direction: column;
        }

        .followButton {
            margin: 1rem 0 0 0;
        }
    }
</style>
